"use client"
import Link from 'next/link'
import type { ReactNode } from 'react'
import { inlinePostText, postTextBlocks, type InlineNode } from '@/lib/social/formatting'
import { cn } from '@/lib/utils'

function inline(nodes: InlineNode[], prefix: string): ReactNode {
  return nodes.map((node, index) => {
    const key = `${prefix}-${index}`, children = node.children ? inline(node.children, key) : node.text
    if (node.type === 'bold') return <strong key={key} className="font-semibold">{children}</strong>
    if (node.type === 'italic') return <em key={key}>{children}</em>
    if (node.type === 'strike') return <s key={key}>{children}</s>
    if (node.type === 'code') return <code key={key} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]">{node.text}</code>
    if (node.type === 'tag') return <Link key={key} href={node.url!} className="font-medium underline-offset-2 hover:underline">{node.text}</Link>
    if (node.type === 'link') return <a key={key} href={node.url} target="_blank" rel="noopener noreferrer nofollow ugc" className="underline underline-offset-3">{node.text}</a>
    return <span key={key}>{node.text}</span>
  })
}

export function RichPostText({ body, className }: { body: string; className?: string }) {
  return <div className={cn('rich-post-text space-y-2 break-words text-base leading-relaxed [overflow-wrap:anywhere]', className)}>{postTextBlocks(body).map((block, index) => {
    const lines = block.lines.map((line, i) => <span key={i}>{i > 0 && <br />}{inline(inlinePostText(line), `${index}-${i}`)}</span>)
    if (!block.lines.some(line => line.trim())) return null
    if (block.type === 'heading') return <h3 key={index} className="pt-1 text-base font-semibold">{lines}</h3>
    if (block.type === 'quote') return <blockquote key={index} className="border-l-2 border-foreground/30 pl-3 text-muted-foreground">{lines}</blockquote>
    if (block.type === 'list') {
      const list = block.lines.map((line, i) => <li key={i}>{inline(inlinePostText(line), `${index}-${i}`)}</li>)
      return block.ordered ? <ol key={index} className="list-decimal space-y-1 pl-5">{list}</ol> : <ul key={index} className="list-disc space-y-1 pl-5">{list}</ul>
    }
    return <p key={index} className="whitespace-pre-wrap">{lines}</p>
  })}</div>
}
